/**
 * Getting one dropped file ready for its slot: is it the right export, how many rows
 * does it hold, and does it need splitting to be accepted.
 *
 * <p>Kept out of the component so the rules — the mismatch message, the two caps, the
 * part arithmetic — are plain functions with plain tests, and so the slot component
 * only renders what this decides.
 */

import { detectExport } from './detectExport';
import { splitCsv, countDataRows, DEFAULT_ROWS_PER_PART } from './splitCsv';
import { slotFor, slotNumber } from './importSlots';

/**
 * The largest file this will send in one request.
 *
 * 🔴 9 MB, and it must stay BELOW the server's cap, not above it. It was 12 MB while
 * application.yml allowed 10 MB, so a file between the two passed this check and was
 * then refused by the server — the one size where the UI promised something it could
 * not deliver. The gap also leaves room for multipart framing, which counts against
 * the server's request limit and does not appear in file.size.
 *
 * ⚠️ Raising this means raising micronaut.server.multipart.max-file-size AND
 * max-request-size first. This number can only ever follow that one down.
 */
export const MAX_UPLOAD_BYTES = 9 * 1024 * 1024;

/**
 * Rows accepted in one upload.
 *
 * 🔑 The byte cap above guards MEMORY on the wire; this guards memory on the SERVER.
 * The importer reads the whole part into a byte[], parses it to rows and then to
 * records, on a -Xmx768m heap shared with four other products. 12,000 rows (4.6 MB) is
 * MEASURED — the jobs export ran cleanly in production on 2026-09-04. 96,000 rows
 * (12 MB), the full Invoice Items export, is not, and a 5.6 MB spreadsheet has already
 * OOM'd a sibling service on this box.
 *
 * So: comfortably above what is proven, comfortably below what is known to be too much.
 * The honest fix is to stream the parse so memory stops scaling with file size; until
 * then, splitting is what makes the biggest file possible at all — and it now happens
 * here, without being asked.
 */
export const MAX_ROWS_PER_UPLOAD = 25000;

/**
 * How full a part may be before the byte cap is considered met.
 *
 * A file is split by ROWS, and rows are not all the same width, so a part sized to
 * exactly 9 MB on average lands over it on the wide half of the file. Aiming at 80%
 * leaves room for that.
 */
const BYTE_HEADROOM = 0.8;

/**
 * One file in a slot, checked and counted but NOT yet split.
 *
 * <p>Splitting is a separate step — {@link splitSlotFile} — so the slot can show the
 * file as it was dropped, say it is over the caps, and offer the split as a visible
 * choice. Start splits anything still over the caps as a fallback, so the choice is
 * about seeing, not about safety.
 *
 * @param {File} file the dropped file
 * @param {string} kind the slot's export kind
 * @param {number} added the order it was added in, for files with no part number
 * @returns {Promise<SlotFile>}
 * @throws {Error} with a message a person can act on, when the file is the wrong
 *                 export or cannot be read as one at all
 *
 * @typedef {{id: string, file: File, rows: number|null, parts: Array<{file: File, rows: number}>,
 *            needsSplit: boolean, split: boolean, part: {n: number, m: number}|null,
 *            added: number}} SlotFile
 */
export async function prepareSlotFile(file, kind, added = 0) {
    const detected = await detectExport(file);
    if (detected !== kind) {
        throw new Error(mismatchMessage(detected));
    }

    // A file we cannot count is not a file we should refuse here: the server reads it
    // too, and it says something useful about why. Null means the row cap simply
    // does not apply and the file goes whole.
    let rows = null;
    try {
        rows = await countDataRows(file);
    } catch {
        rows = null;
    }

    return {
        id: `${file.name}:${file.size}:${added}`,
        file,
        rows,
        parts: [{ file, rows: rows ?? 0 }],
        needsSplit: partsFor(file.size, rows) > 1,
        split: false,
        part: partOrder(file.name),
        added,
    };
}

/**
 * Cut a slot file that is over the caps into parts, in place in the order.
 *
 * @param {SlotFile} entry
 * @returns {Promise<SlotFile>} the same entry with its parts, or unchanged when it
 *          did not need splitting
 */
export async function splitSlotFile(entry) {
    if (!entry.needsSplit || entry.split) {
        return entry;
    }
    const partsNeeded = partsFor(entry.file.size, entry.rows);
    const rowsPerPart = Math.ceil(entry.rows / partsNeeded);
    const parts = (await splitCsv(entry.file, rowsPerPart)).map((p) => ({
        file: new File([p.blob], p.name, { type: 'text/csv' }),
        rows: p.rows,
    }));
    return { ...entry, parts, split: true };
}

/**
 * The "partNofM" a pre-split export carries in its name, or null.
 *
 * Machanicdesk exports are often split before they get here — "Invoices Summary-Table
 * 1-part1of3.csv" — and dropping three at once hands them over in whatever order the
 * file picker felt like. The name says the order.
 */
export function partOrder(name) {
    const m = /part(\d+)of(\d+)/i.exec(name ?? '');
    return m ? { n: Number(m[1]), m: Number(m[2]) } : null;
}

/**
 * The order a slot's files upload in: by part number where the name has one, and by
 * the order they were added otherwise, with the un-numbered after the numbered.
 *
 * @param {SlotFile[]} entries
 * @returns {SlotFile[]} a sorted copy
 */
export function sortSlotFiles(entries) {
    const key = (e) => (e.part ? e.part.n : Number.MAX_SAFE_INTEGER);
    return [...entries].sort((a, b) => (key(a) - key(b)) || (a.added - b.added));
}

/** Whether this file is already in the slot — the same file dropped twice is one file. */
export function sameFile(a, b) {
    return a.name === b.name && a.size === b.size;
}

/**
 * How many parts a file has to be cut into to pass BOTH caps.
 *
 * <p>Exported for its tests. The two caps are independent — a wide file can be over
 * the bytes and under the rows, a narrow one the reverse — so the answer is whichever
 * needs more, and each part is then under both.
 *
 * @param {number} bytes the file size
 * @param {number|null} rows data rows, or null when the file could not be counted
 */
export function partsFor(bytes, rows) {
    const byBytes = Math.ceil(bytes / (MAX_UPLOAD_BYTES * BYTE_HEADROOM));
    if (rows == null) {
        // Without a row count there is nothing to split BY. Over the byte cap the
        // server will refuse it and say so; under it, it goes whole.
        return 1;
    }
    const byRows = Math.ceil(rows / Math.min(DEFAULT_ROWS_PER_PART, MAX_ROWS_PER_UPLOAD));
    const needed = Math.max(byBytes, byRows);
    return bytes <= MAX_UPLOAD_BYTES && rows <= MAX_ROWS_PER_UPLOAD ? 1 : Math.max(2, needed);
}

/**
 * What to say about a file dropped on the wrong slot.
 *
 * Names what the file IS and where it goes, because "wrong file" leaves the person
 * guessing between nine near-identical exports.
 */
export function mismatchMessage(detected) {
    const slot = detected ? slotFor(detected) : null;
    if (!slot) {
        return 'This is not a Machanicdesk export this product reads. Export the list '
            + 'from Machanicdesk as CSV and try again.';
    }
    return `This is the ${slot.label} export — drop it on slot ${slotNumber(detected)}.`;
}
